import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import FormModal, { Field, inputClass } from '../fieldWork/FormModal';
import InductionContent from './InductionContent';
import inductionService from '../../services/induction';
import { errorMessage } from '../../utils/apiResponse';
import type { AckSubjectType, EquipmentInductionDetail, PackItem, PersonRecord, PersonRef } from '../../types/induction';
import { personLabel } from './inductionLabels';

interface Props {
  person: { ref: PersonRef; name: string | null };
  /** Pack policies still due or updated. */
  policies: PackItem[];
  /** Equipment sign-offs they hold. */
  records: PersonRecord[];
  onClose: () => void;
  onDone: (title: string) => void;
}

type Choice = { type: AckSubjectType; id: number };

const encode = (c: Choice) => `${c.type}:${c.id}`;
function decode(v: string): Choice | null {
  const [type, id] = v.split(':');
  if (!type || !id) return null;
  return { type: type as AckSubjectType, id: parseInt(id, 10) };
}

/**
 * A witnessed acknowledgement: the supervisor's phone is handed to someone
 * (usually with no login), they read the content and type their name, and the
 * record says who witnessed it.
 */
export default function WitnessedAckDialog({ person, policies, records, onClose, onDone }: Props) {
  const [choice, setChoice] = useState<Choice | null>(() => {
    if (policies[0]) return { type: 'policy_document', id: policies[0].id };
    if (records[0]) return { type: 'equipment_induction', id: records[0].induction_id };
    return null;
  });
  const [induction, setInduction] = useState<EquipmentInductionDetail | null>(null);
  const [loadingContent, setLoadingContent] = useState(false);
  const [signedName, setSignedName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (choice?.type !== 'equipment_induction') { setInduction(null); return; }
    let live = true;
    setLoadingContent(true);
    inductionService.get(choice.id)
      .then(d => { if (live) setInduction(d); })
      .catch(e => { if (live) setError(errorMessage(e, 'Could not load the induction')); })
      .finally(() => { if (live) setLoadingContent(false); });
    return () => { live = false; };
  }, [choice]);

  const policy = choice?.type === 'policy_document' ? policies.find(p => p.id === choice.id) ?? null : null;
  const record = choice?.type === 'equipment_induction' ? records.find(r => r.induction_id === choice.id) ?? null : null;
  const version = policy ? policy.ack_version : induction?.content_version ?? null;
  const title = policy?.title ?? record?.induction_title ?? '';

  const submit = async () => {
    if (!choice || !version) return;
    if (signedName.trim().length < 2) { setError('They need to type their name.'); return; }
    setSaving(true);
    setError(null);
    try {
      await inductionService.acknowledgeWitnessed(person.ref, choice.type, choice.id, version, signedName.trim());
      onDone(title);
    } catch (e) {
      setError(errorMessage(e, 'Could not record the acknowledgement'));
    } finally {
      setSaving(false);
    }
  };

  const nothing = policies.length === 0 && records.length === 0;

  return (
    <FormModal
      title="Witnessed acknowledgement"
      onClose={onClose}
      onSubmit={submit}
      submitLabel="Record acknowledgement"
      submitting={saving}
      submitDisabled={!choice || !version || signedName.trim().length < 2}
      error={error}
    >
      {nothing ? (
        <p className="text-sm text-neutral-600 break-words">
          {personLabel(person.name)} has nothing to acknowledge: no induction-pack policies are due and they hold no
          equipment sign-offs.
        </p>
      ) : (
        <>
          <p className="text-sm text-neutral-600 break-words">
            Hand your phone to <strong>{personLabel(person.name)}</strong>. They read it and type their name; you are
            recorded as the witness.
          </p>
          <Field label="What they are acknowledging" htmlFor="witness-subject">
            <select
              id="witness-subject"
              className={inputClass}
              value={choice ? encode(choice) : ''}
              onChange={e => { setError(null); setChoice(decode(e.target.value)); }}
            >
              {policies.length > 0 && (
                <optgroup label="Induction pack">
                  {policies.map(p => <option key={`p${p.id}`} value={encode({ type: 'policy_document', id: p.id })}>{p.title}</option>)}
                </optgroup>
              )}
              {records.length > 0 && (
                <optgroup label="Equipment">
                  {records.map(r => (
                    <option key={`e${r.id}`} value={encode({ type: 'equipment_induction', id: r.induction_id })}>{r.induction_title}</option>
                  ))}
                </optgroup>
              )}
            </select>
          </Field>

          <div className="rounded-lg border border-neutral-200 p-3 max-h-72 overflow-y-auto">
            {policy && (
              <div className="space-y-2 text-sm text-neutral-700">
                <p className="font-semibold text-neutral-900 break-words">{policy.title}</p>
                {policy.version_label && <p className="text-xs text-neutral-500 break-words">Version {policy.version_label}</p>}
                {policy.description && <p className="break-words">{policy.description}</p>}
                <Link to="/hr/whs/documents" target="_blank" className="inline-flex items-center gap-1 text-primary-700 hover:underline">
                  <ExternalLink className="h-4 w-4 shrink-0" /> Open the full policy
                </Link>
              </div>
            )}
            {record && loadingContent && <p className="text-sm text-neutral-500">Loading…</p>}
            {record && induction && (
              <InductionContent induction={induction} showPracticalChecks={false} requiredCertifications={induction.required_certifications} />
            )}
          </div>

          <Field label="Their name" htmlFor="witness-name" hint="Typed by the person acknowledging, as their signature.">
            <input id="witness-name" className={inputClass} value={signedName} onChange={e => setSignedName(e.target.value)} autoComplete="off" />
          </Field>
        </>
      )}
    </FormModal>
  );
}
